/**
 * Per-app Remote Config key arrays.
 * Keep in sync with parameters defined in the Firebase Console.
 * Boolean-style flags use "0" / "1" strings (same convention as the native SDK).
 */

export const WEB_REMOTE_CONFIG_KEYS = [
  "use_discover_api",
  "show_intelligence",
  "show_pulse_check",
  "allow_assessment_pdf_download",
  // Which businesses' Intelligence chat goes to agent-brain instead of apiChat
  // (docs/ai-platform/09, phase 09f): a comma-separated list of business ids, or
  // "*" for every business. Empty = nobody. The brain's URL is the build-time
  // VITE_BRAIN_CHAT_API_URL; without it the flag is ignored.
  "intelligence_chat_brain_businesses",
] as const;

export const MOBILE_REMOTE_CONFIG_KEYS = [
  "notification_preferences",
  "use_discover_api",
  "show_pay_transparency",
  "candidate_referrals",
  "show_lead_ads",
  "show_tutorial_cards",
  "show_intelligence",
  "show_ai_coach",
  "use_revenuecat_paywall",
  "use_coach_brain",
] as const;

export type WebRemoteConfigKey = (typeof WEB_REMOTE_CONFIG_KEYS)[number];
export type MobileRemoteConfigKey = (typeof MOBILE_REMOTE_CONFIG_KEYS)[number];

/** Union of all per-app keys. */
export type RemoteConfigParamKey = WebRemoteConfigKey | MobileRemoteConfigKey;

/**
 * Per-application default values (offline / before first fetch).
 * Each app only defines defaults for its own keys.
 */
export const REMOTE_CONFIG_DEFAULTS_BY_APP = {
  web: {
    use_discover_api: "0",
    show_intelligence: "0",
    show_pulse_check: "1",
    // Off unless the Console says otherwise: assessment reports carry
    // personal test data, so the download action stays hidden until a
    // business is explicitly opted in.
    allow_assessment_pdf_download: "0",
    intelligence_chat_brain_businesses: "",
  } satisfies Record<WebRemoteConfigKey, string>,
  mobile: {
    notification_preferences: "0",
    use_discover_api: "0",
    show_pay_transparency: "0",
    candidate_referrals: "0",
    show_lead_ads: "0",
    show_tutorial_cards: "0",
    show_intelligence: "0",
    show_ai_coach: "0",
    use_revenuecat_paywall: "1",
    // Off by default: the AI-coach keeps talking to `apiCandidateChat` until
    // the agent-brain port (docs/ai-platform/06) is switched on per
    // environment, and only where EXPO_PUBLIC_CANDIDATE_CHAT_BRAIN_URL is set.
    use_coach_brain: "0",
  } satisfies Record<MobileRemoteConfigKey, string>,
};
